import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, Linkedin, Wand2, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";

const statusColor = {
  positive: "bg-emerald-500/15 text-emerald-300 border-emerald-400/20",
  pricing: "bg-amber-500/15 text-amber-300 border-amber-400/20",
  later: "bg-slate-500/15 text-slate-300 border-white/10",
  meeting_booked: "bg-sky-500/15 text-sky-300 border-sky-400/20",
  unsubscribe: "bg-rose-500/15 text-rose-300 border-rose-400/20",
};

export default function Inbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/inbox").then((r) => { setThreads(r.data); setActive(r.data[0]); });
  }, []);

  useEffect(() => { setReply(null); }, [active?.thread_id]);

  const generateReply = async () => {
    if (!active) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/inbox/ai-reply", {
        thread_id: active.thread_id,
        lead_message: active.last_message,
        ai_status: active.ai_status,
      });
      setReply(data);
    } catch {
      toast.error("Reply generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const sendVoiceNote = async () => {
    if (!reply?.reply) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/voice-note", { text: reply.reply, voice: "nova", model: "tts-1" });
      const audio = new Audio(`${BACKEND_URL}${data.url}`);
      audio.play();
      toast.success("Voice note generated & playing");
    } catch {
      toast.error("Voice generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="inbox-view">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Unified smart inbox</h1>
        <p className="mt-1 text-sm text-slate-400">AI drafts replies with a Calendly link when the intent is positive.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 min-h-[560px]">
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
          {threads.map((t) => (
            <button key={t.thread_id} onClick={() => setActive(t)} data-testid={`inbox-thread-${t.thread_id}`} className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition ${active?.thread_id === t.thread_id ? "bg-white/[0.04]" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {t.channel === "email" ? <Mail className="w-3.5 h-3.5 text-slate-500" /> : <Linkedin className="w-3.5 h-3.5 text-slate-500" />}
                  <span className="font-medium text-sm truncate">{t.lead_name}</span>
                </div>
                {t.unread && <span className="w-2 h-2 rounded-full bg-sky-400" />}
              </div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{t.company}</div>
              <div className="text-xs text-slate-400 mt-1.5 line-clamp-1">{t.last_message}</div>
              <div className="mt-2"><Badge variant="outline" className={`text-[10px] font-mono ${statusColor[t.ai_status]}`}>{t.ai_status.replace("_", " ")}</Badge></div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 rounded-2xl border border-white/5 bg-white/[0.02] p-6">
          {active ? (
            <>
              <div className="flex items-center justify-between border-b border-white/5 pb-4">
                <div>
                  <div className="font-semibold">{active.lead_name}</div>
                  <div className="text-xs text-slate-500">{active.company} · via {active.channel}</div>
                </div>
                <Badge className={statusColor[active.ai_status]}>{active.ai_status.replace("_", " ")}</Badge>
              </div>
              <div className="mt-4 space-y-3">
                {active.messages.map((m, i) => (
                  <div key={i} className={`flex ${m.from === "ai" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-md rounded-2xl px-4 py-2.5 text-sm ${m.from === "ai" ? "bg-sky-500/10 border border-sky-400/20 text-sky-100" : "bg-white/[0.05] border border-white/10 text-slate-100"}`}>
                      <div className="text-[10px] uppercase font-mono tracking-widest mb-1 opacity-70">{m.from === "ai" ? "AI BDR" : "Prospect"}</div>
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl border border-dashed border-white/10 p-4" data-testid="inbox-ai-draft-reply">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase font-mono tracking-widest text-emerald-300 flex items-center gap-1"><Wand2 className="w-3 h-3" /> AI reply</div>
                  {!reply && (
                    <Button size="sm" onClick={generateReply} disabled={generating} data-testid="inbox-generate-reply-btn" className="h-7 text-xs bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold">
                      {generating ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Drafting…</> : "Draft reply"}
                    </Button>
                  )}
                </div>
                {reply ? (
                  <>
                    <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid="inbox-reply-text">{reply.reply}</div>
                    {reply.auto_bookable && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-emerald-300">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>Calendly link auto-attached — <a href={reply.calendly_url} target="_blank" rel="noopener noreferrer" className="underline">{reply.calendly_url}</a></span>
                      </div>
                    )}
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" onClick={() => toast.success("Reply queued")} data-testid="inbox-send-reply-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-8">Send</Button>
                      <Button size="sm" variant="outline" onClick={sendVoiceNote} disabled={generating} data-testid="inbox-voice-btn" className="border-white/10 bg-white/[0.02] h-8">
                        {generating ? "Generating…" : "🔊 Voice note"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={generateReply} disabled={generating} className="border-white/10 bg-white/[0.02] h-8">Regenerate</Button>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-400">Click <strong>Draft reply</strong> — the AI will match the intent and attach a Calendly link if positive.</div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-slate-500 py-12">Select a conversation.</div>
          )}
        </div>
      </div>
    </div>
  );
}
